/**
 * Beast API Adapter — unified interface for all backend calls.
 * 
 * In Electron: uses IPC via window.beast.invoke()
 * In browser: uses fetch() to gateway + Next.js API routes (fallback)
 * 
 * This is the ONLY file that knows about the transport layer.
 * All UI code calls api.projects.list(), api.chat.send(), etc.
 */

declare global {
  interface Window {
    beast?: {
      invoke: (channel: string, data?: unknown) => Promise<any>;
      onChatEvent: (callback: (event: unknown) => void) => () => void;
      onEvent: (callback: (event: unknown) => void) => () => void;
      platform: string;
      versions: { electron: string; node: string; chrome: string };
    };
  }
}

export const isElectron = typeof window !== 'undefined' && !!window.beast;

// Browser → always go through the same-origin Next.js proxy at /api/gw,
// so it works whether the dashboard is served locally, via cloudflared, or
// via Tailscale Funnel. The proxy itself targets BEAST_GATEWAY_URL on the
// server side (defaults to http://localhost:18790).
const GATEWAY_URL =
  typeof window === 'undefined'
    ? process.env.NEXT_PUBLIC_GATEWAY_URL || 'http://localhost:18790'
    : '/api/gw';

// --- Low-level transport ---

async function ipc(channel: string, data?: unknown): Promise<any> {
  if (isElectron) {
    return window.beast!.invoke(channel, data);
  }
  throw new Error(`IPC not available: ${channel}`);
}

async function gwFetch(path: string, options?: RequestInit): Promise<any> {
  // Server-side calls hit the gateway directly and need the auth token; browser
  // calls go through /api/gw, where the proxy injects it.
  const opts =
    typeof window === 'undefined' && process.env.GATEWAY_TOKEN
      ? { ...options, headers: { ...(options?.headers as Record<string, string>), Authorization: `Bearer ${process.env.GATEWAY_TOKEN}` } }
      : options;
  const res = await fetch(`${GATEWAY_URL}${path}`, opts);
  return res.json();
}

async function apiFetch(path: string, options?: RequestInit): Promise<any> {
  const res = await fetch(`/api${path}`, options);
  return res.json();
}

// --- Gateway calls (chat, session, soma, brain, factory Pi SDK) ---

export const gateway = {
  health: () => isElectron ? ipc('health') : gwFetch('/health'),

  session: () => isElectron ? ipc('session') : gwFetch('/session'),
  sessionNew: () => isElectron ? ipc('session:new') : gwFetch('/session/new', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' }),

  usage: () => isElectron ? ipc('usage') : gwFetch('/usage'),
  models: () => isElectron ? ipc('models') : gwFetch('/models'),
  modelSwitch: (provider: string, id: string) => isElectron
    ? ipc('model:switch', { provider, id })
    : gwFetch('/model', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ provider, id }) }),

  abort: () => isElectron ? ipc('abort') : gwFetch('/abort', { method: 'POST' }),

  chatLast: () => isElectron ? ipc('chat:last') : gwFetch('/chat/last'),

  // Erase one message from the MODEL's memory (SDK context tree + disk jsonl).
  // Separate from chatHistory: that store is only what the UI renders, so
  // deleting there alone leaves the model still remembering the message.
  //   main=true  → the main session (Electron IPC, or gateway POST /forget)
  //   main=false → a project session (gateway POST /sessions/<slug>/forget)
  forgetMessage: (
    slug: string,
    msg: { role: string; text: string; timestamp?: number },
    opts?: { main?: boolean; electronMain?: boolean },
  ) => {
    if (opts?.electronMain && isElectron) return ipc('chat:forget', msg);
    const path = opts?.main ? '/forget' : `/sessions/${encodeURIComponent(slug)}/forget`;
    return gwFetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(msg),
    });
  },

  chatHistory: {
    read: (slug: string) => isElectron
      ? ipc('chat:history:read', { slug })
      : gwFetch(`/chat/history?slug=${encodeURIComponent(slug)}`),
    write: (slug: string, messages: unknown[]) => isElectron
      ? ipc('chat:history:write', { slug, messages })
      : gwFetch('/chat/history', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ slug, messages }) }),
  },

  projectSwitch: (data: { path: string; name?: string; slug?: string }) => isElectron
    ? ipc('project:switch', data)
    : gwFetch('/project/switch', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }),

  upload: (data: { data: string; media_type: string; name?: string }) => isElectron
    ? ipc('upload', data)
    : gwFetch('/upload', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }),

  brain: {
    prompt: () => isElectron ? ipc('brain:prompt') : gwFetch('/brain/prompt'),
    tree: () => isElectron ? ipc('brain:tree') : gwFetch('/brain/tree'),
    file: (path: string) => isElectron
      ? ipc('brain:file', { path })
      : gwFetch(`/brain/file?path=${encodeURIComponent(path)}`),
  },
};

// --- Session builtins (SDK methods — not Soma reimplementation) ---

export const sessionApi = {
  compact: () => ipc('session:compact'),
  reload: () => ipc('session:reload'),
  exportSession: (format?: string) => ipc('session:export', { format }),
};

// --- Soma (read-only data for UI panels — commands go through chat) ---

export const soma = {
  maps: () => isElectron ? ipc('soma:maps') : gwFetch('/soma/maps'),
  map: (name: string) => isElectron
    ? ipc('soma:map', { name })
    : gwFetch(`/soma/map?name=${encodeURIComponent(name)}`),
  protocols: () => isElectron ? ipc('soma:protocols') : gwFetch('/soma/protocols'),
  muscles: () => isElectron ? ipc('soma:muscles') : gwFetch('/soma/muscles'),
  preloads: () => isElectron ? ipc('soma:preloads') : gwFetch('/soma/preloads'),
  identity: () => isElectron ? ipc('soma:identity') : gwFetch('/soma/identity'),
  sessions: (projectPath?: string) => isElectron
    ? ipc('soma:sessions', { projectPath })
    : gwFetch(`/soma/sessions${projectPath ? `?projectPath=${encodeURIComponent(projectPath)}` : ''}`),
  loadSession: (file: string, projectPath?: string) => isElectron
    ? ipc('soma:session:load', { file, projectPath })
    : gwFetch(`/soma/session?file=${encodeURIComponent(file)}${projectPath ? `&projectPath=${encodeURIComponent(projectPath)}` : ''}`),
  doctor: () => isElectron ? ipc('soma:doctor') : gwFetch('/soma/doctor'),
  scratchRead: () => isElectron ? ipc('soma:scratch:read') : gwFetch('/soma/scratch'),
  scratchWrite: (content: string) => isElectron
    ? ipc('soma:scratch:write', { content })
    : gwFetch('/soma/scratch', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ content }) }),
  scratchAppend: (note: string) => isElectron
    ? ipc('soma:scratch:append', { note })
    : gwFetch('/soma/scratch', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note }) }),
  settings: () => isElectron ? ipc('soma:settings') : gwFetch('/soma/settings'),
  updateSetting: (path: string, value: any) => isElectron
    ? ipc('soma:settings:update', { path, value })
    : gwFetch('/soma/settings/update', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ path, value }) }),
};

// --- Chat (streaming) ---
// In Electron: IPC invoke + event listeners
// In browser: SSE fetch (existing pattern)

export const chat = {
  /** Send a message. In Electron, streaming events come via onChatEvent. */
  send: (data: { message: string; context?: string; images?: any[]; streamingBehavior?: string }) =>
    isElectron
      ? ipc('chat:send', data)
      : undefined, // Browser uses direct SSE fetch in useChat.ts

  /** Subscribe to chat streaming events (Electron only) */
  onChatEvent: (callback: (event: any) => void): (() => void) => {
    if (isElectron) return window.beast!.onChatEvent(callback);
    return () => {}; // No-op in browser
  },

  /** Subscribe to general beast events (keepalives, notifications) */
  onEvent: (callback: (event: any) => void): (() => void) => {
    if (isElectron) return window.beast!.onEvent(callback);
    return () => {};
  },
};

// --- Projects ---

export const projectsApi = {
  list: (opts?: { limit?: number; offset?: number }) => isElectron
    ? ipc('projects:list', opts)
    : apiFetch('/projects'),
  get: (slug: string) => isElectron
    ? ipc('projects:get', { slug })
    : apiFetch(`/projects/${slug}`),
  create: (data: { name: string; category: string; description?: string; goal?: string; icon?: string; collab?: string; path?: string }) => isElectron
    ? ipc('projects:create', data)
    : apiFetch('/projects', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }),
  update: (slug: string, updates: Record<string, unknown>) => isElectron
    ? ipc('projects:update', { slug, updates })
    : apiFetch(`/projects/${slug}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(updates) }),
  touch: (slug: string) => isElectron
    ? ipc('projects:touch', { slug })
    : apiFetch(`/projects/${slug}/touch`, { method: 'POST' }),
  actions: (slug: string, action: string) => isElectron
    ? ipc('projects:actions', { slug, action })
    : apiFetch(`/projects/${slug}/actions`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action }) }),
  preload: (slug: string, method: 'get' | 'put' = 'get', preload?: string) => isElectron
    ? ipc('projects:preload', { slug, method, preload })
    : method === 'get'
      ? apiFetch(`/projects/${slug}/preload`)
      : apiFetch(`/projects/${slug}/preload`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ preload }) }),
  variants: (slug: string) => isElectron
    ? ipc('projects:variants', { slug })
    : apiFetch(`/projects/${slug}/variants`),
};

// --- Tasks + Milestones ---

export const tasksApi = {
  create: (data: { projectId: string; milestoneId?: string; title: string; executor?: string; energy?: string }) => isElectron
    ? ipc('tasks:create', data)
    : apiFetch('/tasks', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }),
  get: (id: string) => isElectron ? ipc('tasks:get', { id }) : apiFetch(`/tasks/${id}`),
  update: (id: string, updates: Record<string, unknown>) => isElectron
    ? ipc('tasks:update', { id, updates })
    : apiFetch(`/tasks/${id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(updates) }),
  delete: (id: string) => isElectron
    ? ipc('tasks:delete', { id })
    : apiFetch(`/tasks/${id}`, { method: 'DELETE' }),
  reorder: (taskIds: string[]) => isElectron
    ? ipc('tasks:reorder', { taskIds })
    : apiFetch('/tasks/reorder', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ taskIds }) }),
};

export const milestonesApi = {
  create: (data: { projectId: string; name: string; position?: number }) => isElectron
    ? ipc('milestones:create', data)
    : apiFetch('/milestones', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }),
  update: (id: string, updates: Record<string, unknown>) => isElectron
    ? ipc('milestones:update', { id, updates })
    : apiFetch(`/milestones/${id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(updates) }),
  delete: (id: string) => isElectron
    ? ipc('milestones:delete', { id })
    : apiFetch(`/milestones/${id}`, { method: 'DELETE' }),
};

// --- Content ---

export const contentApi = {
  posts: (opts?: { projectId?: string; status?: string }) => isElectron
    ? ipc('content:posts', opts)
    : apiFetch(`/content/posts${opts?.projectId ? `?projectId=${opts.projectId}` : ''}${opts?.status ? `${opts?.projectId ? '&' : '?'}status=${opts.status}` : ''}`),
  postsAll: (status?: string) => isElectron
    ? ipc('content:posts:all', { status })
    : apiFetch(`/content/posts/all${status ? `?status=${status}` : ''}`),
  post: (id: string) => isElectron ? ipc('content:post', { id }) : apiFetch(`/content/posts/${id}`),
  create: (data: any) => isElectron
    ? ipc('content:create', data)
    : apiFetch('/content/posts', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }),
  update: (id: string, updates: Record<string, unknown>) => isElectron
    ? ipc('content:update', { id, updates })
    : apiFetch(`/content/posts/${id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(updates) }),
  delete: (id: string) => isElectron
    ? ipc('content:delete', { id })
    : apiFetch(`/content/posts/${id}`, { method: 'DELETE' }),
  generate: (data: any) => isElectron
    ? ipc('content:generate', data)
    : apiFetch('/content/generate', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }),
  generateAudio: (data?: any) => isElectron
    ? ipc('content:generate-audio', data)
    : data ? apiFetch('/content/generate-audio', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }) : apiFetch('/content/generate-audio'),
  generateVideo: (data: any) => isElectron
    ? ipc('content:generate-video', data)
    : apiFetch('/content/generate-video', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }),
  studioStart: (data: any) => isElectron ? ipc('content:studio-start', data) : apiFetch('/content/studio-start', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }),
  studioEdit: (data: any) => isElectron ? ipc('content:studio-edit', data) : apiFetch('/content/studio-edit', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }),
  studioAudio: (data: any) => isElectron ? ipc('content:studio-audio', data) : apiFetch('/content/studio-audio', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }),
  compositions: () => isElectron ? ipc('content:compositions') : apiFetch('/content/compositions'),
  audioLoop: (data: any) => isElectron ? ipc('content:audio-loop', data) : apiFetch('/content/audio/loop', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }),
};

// --- SEO ---

// SEO always uses API routes (Next.js runs in both browser and Electron)
// This avoids duplicating the complex query logic in IPC handlers
export const seoApi = {
  domains: () => apiFetch('/seo/domains'),
  overview: () => apiFetch('/seo/overview'),
  domain: (domain: string) => apiFetch(`/seo/${domain}`),
};

// --- Servers ---

export const serversApi = {
  list: () => isElectron ? ipc('servers:list') : apiFetch('/servers'),
  status: (projectId: string) => isElectron ? ipc('servers:status', { projectId }) : apiFetch(`/servers/status?projectId=${projectId}`),
  start: (projectId: string) => isElectron
    ? ipc('servers:start', { projectId })
    : apiFetch('/servers/start', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ projectId }) }),
  stop: (projectId: string) => isElectron
    ? ipc('servers:stop', { projectId })
    : apiFetch('/servers/stop', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ projectId }) }),
};

// --- Portfolio ---

// Portfolio always uses API routes (avoids IPC data shape mismatches)
export const portfolioApi = {
  list: () => apiFetch('/portfolio'),
  screenshot: (slug: string) => apiFetch(`/portfolio/screenshot?slug=${slug}`),
  start: (slug: string, port: number, path?: string) => apiFetch('/portfolio/start', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ slug, port, path }) }),
};

// --- Factory ---

export const factoryApi = {
  status: (domain?: string) => isElectron
    ? ipc('factory-api:status')
    : apiFetch(`/factory/status${domain ? `?domain=${domain}` : ''}`),
  build: (data: any) => isElectron
    ? ipc('factory-api:build', data)
    : apiFetch('/factory/build', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }),
  resume: (pipelineId: string) => isElectron
    ? ipc('factory-api:resume', { pipelineId })
    : apiFetch('/factory/resume', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ pipelineId }) }),
  deleteBuild: (domain: string) => isElectron
    ? ipc('factory-api:build', { action: 'delete', domain })
    : apiFetch('/factory/build', { method: 'DELETE', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ domain }) }),
};

// --- Resolve ---

export const resolveApi = {
  list: () => isElectron ? ipc('resolve:list') : apiFetch('/resolve'),
  projects: () => isElectron ? ipc('resolve:projects') : apiFetch('/resolve/projects'),
  project: (name: string) => isElectron ? ipc('resolve:project', { name }) : apiFetch(`/resolve/projects/${name}`),
  timeline: (name: string, timeline: string) => isElectron ? ipc('resolve:timeline', { name, timeline }) : apiFetch(`/resolve/projects/${name}/timelines/${timeline}`),
  pipeline: () => isElectron ? ipc('resolve:pipeline') : apiFetch('/resolve/pipeline'),
  execute: (data: any) => isElectron ? ipc('resolve:execute', data) : apiFetch('/resolve/execute', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }),
  launch: () => isElectron ? ipc('resolve:launch') : apiFetch('/resolve/launch', { method: 'POST' }),
  effects: () => isElectron ? ipc('resolve:effects') : apiFetch('/resolve/effects'),
};
